import { useCallback } from 'react';

import type { OnBuildPriceSeenEvent, OnTrackPriceSelected } from '../types';
import type { PriceContext } from '@hotels-hotel-detail/utils/types';
import type { MiniEventTracker } from '@shared/types/frontend/mini-event-tracker';

import {
  buildPriceSeenEventProps,
  buildPriceSelectedEventProps,
} from '@hotels-hotel-detail/utils/hotel-offers';
import {
  CLICK_AREA,
  PANEL_POSITION,
} from '@shared/utils-frontend/mini-events/constants';
import { buildAdditionalInfoPriceSelectedInfo } from '@shared/utils-frontend/mini-events/hotel-prices';
import { buildPriceSeenInfo } from '@shared/utils-frontend/mini-events/hotels-seen';

const useEventHandlers = (
  priceContext: PriceContext,
  currency: string,
  miniEventTracker: MiniEventTracker,
  isMobile?: boolean,
) => {
  const buildSeenEvent = useCallback(
    ({
      offer,
      partner,
      partnerRanking,
    }: Parameters<OnBuildPriceSeenEvent>[0]) => {
      const eventProps = buildPriceSeenEventProps({
        offer,
        partner,
        partnerRanking,
        isCugAreaAvailable: true,
        isAmenityAreaAvailable: true,
        panelPosition: PANEL_POSITION.BOOKING_PANEL,
        priceContext,
        currency,
      });

      miniEventTracker.trackHotelPriceSeen(buildPriceSeenInfo(eventProps));
    },
    [priceContext, currency, miniEventTracker],
  );

  const trackSelectedEvent = useCallback(
    ({
      clickArea = CLICK_AREA.NORMAL_RATE_CARD,
      offer,
      offerPosition,
      partner,
      partnerRanking,
      position,
    }: Parameters<OnTrackPriceSelected>[0]) => {
      if (partnerRanking === 1 && offerPosition === 1) {
        miniEventTracker.trackSmartMetric('hero_price_card_click');
      }

      miniEventTracker.trackSmartMetric(
        `Details_Redirect_${isMobile ? 'MWeb' : 'Desktop'}`,
      );

      const eventProps = buildPriceSelectedEventProps({
        offer,
        partner,
        partnerRanking,
        isCugAreaAvailable: true,
        isAmenityAreaAvailable: true,
        clickArea,
        hasRedirect: true,
        priceContext,
        currency,
        offerPosition,
        position,
      });

      miniEventTracker.trackHotelPricesAction(
        buildAdditionalInfoPriceSelectedInfo(eventProps),
      );
    },
    [priceContext, currency, miniEventTracker, isMobile],
  );

  return { buildSeenEvent, trackSelectedEvent };
};

export default useEventHandlers;
